import { create } from "zustand";
import { devtools as devtoolsMiddleware } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import { FILE_ONLY_PLACEHOLDER } from "../types/chat";
import type {
  UIMessage,
  DocumentAttachment,
  ConversationMessage,
} from "../types/chat";

const devtools = import.meta.env.DEV
  ? devtoolsMiddleware
  : (((fn: unknown) => fn) as typeof devtoolsMiddleware);

interface ChatState {
  threadId: string;
  threadName: string | null;
  messages: UIMessage[];
  isStreaming: boolean;
  isLoadingHistory: boolean;
  lastUserInput: { text: string; documents?: DocumentAttachment[] } | null;
  jdPath: string | null;
  isNewThread: boolean;

  // Actions
  addUserMessage: (content: string, documents?: DocumentAttachment[]) => void;
  addThinkingPlaceholder: () => void;
  appendStreamToken: (token: string) => void;
  completeTurn: (
    finalContent: string,
    threadName: string | null,
    jdPath?: string | null
  ) => void;
  resetStreaming: () => void;
  setError: (message: string, rawDetails?: string) => void;
  stopGeneration: () => void;
  removeLastErrorMessage: () => void;
  startNewThread: (existingThreadId?: string) => void;
  loadThread: (
    threadId: string,
    threadName: string | null,
    messages: ConversationMessage[]
  ) => void;
  setLoadingHistory: (loading: boolean) => void;
  setJdPath: (path: string | null) => void;
}

export const useChatStore = create<ChatState>()(
  devtools(
    (set) => ({
      threadId: uuidv4(),
      threadName: null,
      messages: [],
      isStreaming: false,
      isLoadingHistory: false,
      lastUserInput: null,
      jdPath: null,
      isNewThread: true,

      addUserMessage: (content, documents) =>
        set(
          (state) => ({
            isNewThread: false,
            lastUserInput: { text: content, ...(documents && { documents }) },
            messages: [
              ...state.messages,
              {
                id: uuidv4(),
                role: "user",
                content,
                status: "complete",
                timestamp: new Date().toISOString(),
                ...(documents && { documents }),
              },
            ],
          }),
          undefined,
          "chat/addUserMessage"
        ),

      addThinkingPlaceholder: () =>
        set(
          (state) => ({
            isStreaming: true,
            messages: [
              ...state.messages,
              {
                id: uuidv4(),
                role: "assistant",
                content: "",
                status: "streaming",
                timestamp: new Date().toISOString(),
              },
            ],
          }),
          undefined,
          "chat/addThinkingPlaceholder"
        ),

      appendStreamToken: (token) =>
        set(
          (state) => {
            if (!token) return state;
            const messages = [...state.messages];
            const last = messages[messages.length - 1];
            if (last?.role === "assistant" && last.status === "streaming") {
              messages[messages.length - 1] = {
                ...last,
                content: last.content + token,
              };
            }
            return { messages, isStreaming: true };
          },
          false,
          "chat/appendStreamToken"
        ),

      resetStreaming: () =>
        set(
          (state) => {
            const messages = [...state.messages];
            const last = messages[messages.length - 1];
            if (last?.role === "assistant" && last.status === "streaming") {
              messages[messages.length - 1] = {
                ...last,
                content: last.content || "Connection lost. Please try again.",
                status: "error",
              };
            }
            return { messages, isStreaming: false };
          },
          false,
          "chat/resetStreaming"
        ),

      stopGeneration: () =>
        set(
          (state) => {
            const messages = [...state.messages];
            const last = messages[messages.length - 1];
            if (last?.role === "assistant" && last.status === "streaming") {
              messages[messages.length - 1] = {
                ...last,
                content: last.content || "Generation stopped.",
                status: "complete",
              };
            }
            return { messages, isStreaming: false };
          },
          false,
          "chat/stopGeneration"
        ),

      completeTurn: (finalContent, threadName, jdPath) =>
        set(
          (state) => {
            const messages = [...state.messages];
            let lastAssistantIndex = -1;
            for (let i = messages.length - 1; i >= 0; i--) {
              if (messages[i].role === "assistant") {
                lastAssistantIndex = i;
                break;
              }
            }
            if (lastAssistantIndex !== -1) {
              messages[lastAssistantIndex] = {
                ...messages[lastAssistantIndex],
                content: finalContent,
                status: "complete",
                ...(jdPath ? { jdPath } : {}),
              };
            }
            return {
              messages,
              isStreaming: false,
              threadName: threadName ?? state.threadName,
              // Only update jdPath if a new value is provided
              ...(jdPath !== undefined && { jdPath: jdPath ?? state.jdPath }),
            };
          },
          undefined,
          "chat/completeTurn"
        ),

      setError: (message, rawDetails) =>
        set(
          (state) => {
            const messages = [...state.messages];
            let lastAssistantIndex = -1;
            for (let i = messages.length - 1; i >= 0; i--) {
              if (messages[i].role === "assistant") {
                lastAssistantIndex = i;
                break;
              }
            }
            if (lastAssistantIndex !== -1) {
              messages[lastAssistantIndex] = {
                ...messages[lastAssistantIndex],
                content: message,
                status: "error",
                ...(rawDetails && { errorDetails: rawDetails }),
              };
            } else {
              // No assistant message to overwrite — append a new error bubble
              messages.push({
                id: uuidv4(),
                role: "assistant",
                content: message,
                status: "error",
                ...(rawDetails && { errorDetails: rawDetails }),
              });
            }
            return { messages, isStreaming: false };
          },
          undefined,
          "chat/setError"
        ),

      removeLastErrorMessage: () =>
        set(
          (state) => {
            let lastErrorIndex = -1;
            for (let i = state.messages.length - 1; i >= 0; i--) {
              if (
                state.messages[i].role === "assistant" &&
                state.messages[i].status === "error"
              ) {
                lastErrorIndex = i;
                break;
              }
            }
            if (lastErrorIndex === -1) return state;
            return {
              messages: state.messages.filter((_, i) => i !== lastErrorIndex),
            };
          },
          undefined,
          "chat/removeLastErrorMessage"
        ),

      startNewThread: (existingThreadId?: string) =>
        set(
          {
            threadId: existingThreadId ?? uuidv4(),
            threadName: null,
            messages: [],
            isStreaming: false,
            isLoadingHistory: false,
            lastUserInput: null,
            jdPath: null,
            isNewThread: true,
          },
          undefined,
          "chat/startNewThread"
        ),

      loadThread: (threadId, threadName, rawMessages) => {
        // Scan for the last message with actual candidates (non-empty list)
        let lastJdPath: string | null = null;
        for (let i = rawMessages.length - 1; i >= 0; i--) {
          const msg = rawMessages[i];
          if (
            msg.job_description_s3_key &&
            (msg.candidates_list_with_scores?.candidates_id_list?.length ?? 0) >
              0
          ) {
            lastJdPath = msg.job_description_s3_key!;
            break;
          }
        }

        return set(
          {
            threadId,
            threadName: threadName ?? null,
            jdPath: lastJdPath,
            messages: rawMessages.map((msg) => {
              const isUser = msg.role === "user" || msg.role === "human";

              // Build documents from cv_s3_keys or job_description_s3_key.
              // JD-fallback documents (no cv_s3_keys present) are marked silent
              // so they do not render as visible file chips in the UI.
              let documents: DocumentAttachment[] | undefined;
              const cvKeys = msg.cv_s3_keys ?? [];
              const jdFallbackKey =
                !msg.cv_s3_keys && msg.job_description_s3_key
                  ? msg.job_description_s3_key
                  : null;
              const s3Keys = jdFallbackKey ? [jdFallbackKey] : cvKeys;
              if (isUser && s3Keys.length > 0) {
                documents = s3Keys.map((s3Key) => {
                  const basename = s3Key.split("/").pop() || "";
                  const match = basename.match(
                    /^\d{8}T\d{6}Z_[a-f0-9-]{36}_(.+)$/
                  );
                  const fileName = match?.[1] || basename;
                  const ext = fileName.split(".").pop()?.toLowerCase();
                  return {
                    document_url: s3Key,
                    file_name: fileName,
                    ...(ext && {
                      document_type: ext as DocumentAttachment["document_type"],
                    }),
                    ...(jdFallbackKey &&
                      s3Key === jdFallbackKey && { silent: true }),
                  };
                });
              }

              // Strip placeholder text for file-only messages
              const isFileOnlyPlaceholder =
                isUser && msg.content === FILE_ONLY_PLACEHOLDER;

              // If placeholder detected but no documents built, add generic indicator
              if (isFileOnlyPlaceholder && !documents) {
                documents = [
                  { document_url: "", file_name: "Attached document" },
                ];
              }

              return {
                id: uuidv4(),
                role: isUser ? ("user" as const) : ("assistant" as const),
                content: isFileOnlyPlaceholder ? "" : msg.content,
                status: "complete" as const,
                ...(msg.timestamp && { timestamp: msg.timestamp }),
                ...(documents && { documents }),
                ...(!isUser &&
                msg.job_description_s3_key &&
                (msg.candidates_list_with_scores?.candidates_id_list?.length ??
                  0) > 0
                  ? { jdPath: msg.job_description_s3_key }
                  : {}),
              };
            }),
            isStreaming: false,
            isLoadingHistory: false,
            isNewThread: false,
          },
          undefined,
          "chat/loadThread"
        );
      },

      setLoadingHistory: (loading) =>
        set({ isLoadingHistory: loading }, false, "chat/setLoadingHistory"),

      setJdPath: (path) => set({ jdPath: path }, false, "chat/setJdPath"),
    }),
    { name: "ChatStore" }
  )
);
